export type VisualGroup<Value extends string | number> = {
  text: string;
  values: readonly Value[];
};

export type RawVisualGrouping = {
  option_groups?: readonly VisualGroup<number>[];
  row_groups?: readonly VisualGroup<string>[];
  column_groups?: readonly VisualGroup<string>[];
};

export type VisualGrouping = {
  optionGroups?: readonly VisualGroup<number>[];
  rowGroups?: readonly VisualGroup<string>[];
  columnGroups?: readonly VisualGroup<string>[];
};

export const decodeVisualGroups = (raw: RawVisualGrouping): VisualGrouping => ({
  ...(raw.option_groups !== undefined
    ? { optionGroups: raw.option_groups }
    : {}),
  ...(raw.row_groups !== undefined ? { rowGroups: raw.row_groups } : {}),
  ...(raw.column_groups !== undefined
    ? { columnGroups: raw.column_groups }
    : {}),
});

export const groupForValue = <Value extends string | number>(
  groups: readonly VisualGroup<Value>[] | undefined,
  value: Value,
) => groups?.find((group) => group.values.includes(value));

export const groupHeading = <Value extends string | number>(
  groups: readonly VisualGroup<Value>[] | undefined,
  value: Value | undefined,
) => groups?.find((group) => group.values[0] === value)?.text;

export const rowGroupSections = <Row>(
  rows: readonly Row[],
  groups: readonly VisualGroup<string>[] | undefined,
  rowId: (row: Row, index: number) => string | undefined,
) => {
  const sections: {
    group: VisualGroup<string> | undefined;
    rows: { row: Row; index: number }[];
  }[] = [];
  rows.forEach((row, index) => {
    const value = rowId(row, index);
    const group =
      value === undefined ? undefined : groupForValue(groups, value);
    const previous = sections[sections.length - 1];
    if (previous && previous.group === group) {
      previous.rows.push({ row, index });
    } else {
      sections.push({ group, rows: [{ row, index }] });
    }
  });
  return sections;
};

export const columnGroupSpans = <Value extends string | number>(
  values: readonly Value[],
  groups: readonly VisualGroup<Value>[],
) => {
  const spans: { text: string; count: number }[] = [];
  let previous: VisualGroup<Value> | undefined;
  for (const value of values) {
    const group = groupForValue(groups, value);
    if (spans.length > 0 && group === previous) {
      spans[spans.length - 1].count += 1;
    } else {
      spans.push({ text: group?.text ?? "", count: 1 });
    }
    previous = group;
  }
  return spans;
};
